/**
 * Types for the "Conceptual Problems" Data Editing page (feature 029).
 *
 * Read-only until the 2026-09-10 amendment, which added exactly two writes: a problem's categories,
 * and the category list.
 *
 * Contract: `specs/029-conceptual-problems-review/contracts/conceptual-problems-api.yaml`.
 *
 * `Hand` and `AuctionBid` are imported from the practice types rather than redeclared — the
 * conceptual page renders its auction and hands through the same shared components, so sharing
 * the shape is what keeps them renderable.
 */
import type { AuctionBid, Hand } from './problems';

/** N, E, S or W. Empty only when a problem records no decision seat at all. */
export type ConceptualSeat = 'N' | 'E' | 'S' | 'W';

/** One topic, with how many conceptual problems are filed under it. */
export interface ConceptualCategory {
  /**
   * `conceptual_categories.id`, or null for the synthetic "Uncategorised" bucket. This is the
   * category segment of the page's URL: one live category name contains suit symbols, so a slug
   * of the name would be lossy.
   */
  id: number | null;
  name: string;
  /** Zero is a real, listed value — an empty topic is shown, not hidden. */
  problemCount: number;
}

/**
 * One category a problem is filed under.
 *
 * Id and name only, deliberately. The database records whether an assignment was PROPOSED by the
 * import's classifier or chosen by an editor, and that never reaches the client: the client asked
 * (2026-09-10) that the two read identically, so an editor is never nudged into deferring to a
 * machine's guess. There is no field here to accidentally render.
 */
export interface ConceptualCategoryRef {
  id: number;
  name: string;
}

/**
 * Who last saved a problem's categories, and when (2026-09-17, FR-047).
 *
 * A name and a time only — no user id reaches the client. Null on a problem no editor has ever
 * saved: a category proposed at import is not anyone's edit.
 */
export interface ConceptualLastEdit {
  firstName: string;
  lastName: string;
  /** ISO timestamp of the save. */
  at: string;
}

/** What a category save gives back. */
export interface SaveCategoriesResponse {
  serial: number;
  /** The problem's categories after the save. Empty means it is now Uncategorised. */
  categories: ConceptualCategoryRef[];
  /**
   * The new problem count of every category the save added to or removed from. The page holds a
   * whole category in memory, so these correct the numbers already on screen without a refetch.
   */
  affectedCounts: ConceptualCategory[];
  /** The category this save created, if the editor named one. */
  createdCategory: ConceptualCategoryRef | null;
  /** The saver and the time of this save, as the next load will show them (FR-047). */
  lastEdit: ConceptualLastEdit | null;
  /** Whether the problem is now locked — always true after a Super Editor's save (FR-048). */
  locked: boolean;
}

/** The body of the 403 a save gets when a Super Editor has locked the problem (FR-048). */
export interface ProblemLockedError {
  code: 'PROBLEM_LOCKED';
  message: string;
  /** The Super Editor who locked it. */
  lastEdit: ConceptualLastEdit | null;
}

/** One seat's hand. A conceptual problem's hands are not implicitly the deciding seat's. */
export interface SeatHand {
  seat: ConceptualSeat;
  isDecisionSeat: boolean;
  hand: Hand;
}

/** One conceptual problem, as the review page shows it. */
export interface ConceptualProblem {
  /**
   * `problems.serial_number` — the problem's identity here and in the URL. The database id is
   * deliberately not on the wire: the serial is what the source notes use and it survives the
   * derived category being recomputed on re-import.
   */
  serial: number;
  scoring: string;
  difficultyLevel: number | null;
  source: string | null;
  /** Empty for roughly nine in ten problems, in which case no indicator is drawn. */
  vulnerability: string;
  vulnerabilityText: string;
  decisionSeat: ConceptualSeat | '';
  /**
   * Every call, verbatim — including the generic-denomination placeholders (`1M`, `2m`, `3x`) and
   * the either/or forms (`4c/4d`) that only this data set carries. A `??` marker arrives already
   * normalised to `?`.
   */
  auction: AuctionBid[];
  /** Every recorded hand, deciding seat first. Empty is the normal case, not an error. */
  hands: SeatHand[];
  question: string | null;
  /** The descriptive answer, shown immediately — this page has no reveal step. */
  answer: string | null;
  /** The problem's component tags, wording exactly as recorded. */
  components: string[];
  /**
   * Every category the problem is filed under, in category-id order. Empty means Uncategorised,
   * which is a real state an editor can choose rather than only a gap in the data (FR-031).
   */
  categories: ConceptualCategoryRef[];
  /** Who last saved this problem's categories; null when no editor ever has (FR-047). */
  lastEdit: ConceptualLastEdit | null;
  /**
   * True once a Super Editor has saved this problem's categories. From then on only a Super Editor
   * may change them; everyone else sees the picker read-only (FR-048). Never cleared.
   */
  locked: boolean;
}

/** The category list. */
export interface ConceptualCategoriesResponse {
  categories: ConceptualCategory[];
}

/** One category's problems. `total` is the N in the page's "k of N". */
export interface ConceptualProblemsResponse {
  categoryId: number | null;
  categoryName: string;
  items: ConceptualProblem[];
  total: number;
}

/** The URL segment that addresses problems with no category. */
export const UNCATEGORISED_SEGMENT = 'uncategorised';

/** Turns a category id (or null) into the segment used in both the URL and the API path. */
export function categorySegment(id: number | null): string {
  return id === null ? UNCATEGORISED_SEGMENT : String(id);
}
